import { useState, useEffect } from 'react';
import { matchRoute } from '../config/routes';
import { AuthError } from '../shared/types';

export const getLoginRedirectFromPath = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  const orgId = segments.length >= 2 ? segments[0] : null;
  const path = orgId ? `/${orgId}/login` : '/login'
  return path + "?error=" + AuthError.SESSION_EXPIRED;
};

// Hook to get URL parameters from the current route
export const useUrlParams = (): Record<string, string> => {
  const [params, setParams] = useState<Record<string, string>>(() => {
    const match = matchRoute(window.location.pathname);
    return match?.params ?? {};
  });

  useEffect(() => {
    const updateParams = () => {
      const match = matchRoute(window.location.pathname);
      setParams(match?.params ?? {});
    };

    updateParams();

    window.addEventListener('popstate', updateParams);
    window.addEventListener('navigate', updateParams);

    return () => {
      window.removeEventListener('popstate', updateParams);
      window.removeEventListener('navigate', updateParams);
    };
  }, []);

  return params;
};

// Hook to get and set search parameters
export const useSearchParams = (): [URLSearchParams, (params: URLSearchParams, options?: { replace?: boolean }) => void] => {
  const [searchParams, setSearchParamsState] = useState(() => new URLSearchParams(window.location.search));

  useEffect(() => {
    const updateSearchParams = () => {
      setSearchParamsState(new URLSearchParams(window.location.search));
    };

    window.addEventListener('popstate', updateSearchParams);
    window.addEventListener('navigate', updateSearchParams);

    return () => {
      window.removeEventListener('popstate', updateSearchParams);
      window.removeEventListener('navigate', updateSearchParams);
    };
  }, []);

  const setSearchParams = (params: URLSearchParams, options?: { replace?: boolean }) => {
    const newSearch = params.toString();
    const currentSearch = searchParams.toString();

    if (newSearch === currentSearch) {
      return;
    }

    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;

    if (options?.replace) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }

    setSearchParamsState(new URLSearchParams(newSearch));
  };

  return [searchParams, setSearchParams];
};
